import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';
import { BlacklistParameters } from '../../hooks/components/useBlacklist/constants';
import { BlacklistCurrent } from '../../models/Blacklist/Blacklist';
import { ReasonTypes } from '../../hooks/components/useBlacklist/types';

export interface BlacklistState {
  items: BlacklistCurrent[];
  totalItemsCount: number;
  status: Status;
}

export interface BlacklistResponse {
  items: BlacklistCurrent[];
  totalItemsCount: number;
}

export interface BlacklistRequest {
  From: string | null;
  To: string | null;
  TextSearch: string;
  Reason: ReasonTypes | null;
  PageIndex: number;
  PageSize: number;
  SortKeySelector: string;
  SortOrder: string;
}

const initialState: BlacklistState = {
  items: [],
  totalItemsCount: 0,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getBlacklist = createAsyncThunk(
  'Blacklist/getBlacklist',
  async (request: BlacklistRequest, { rejectWithValue }) => {
    const requestParams: Record<string, string | number> = {
      [BlacklistParameters.TextSearch]: request.TextSearch,
      [BlacklistParameters.PageIndex]: request.PageIndex,
      [BlacklistParameters.PageSize]: request.PageSize,
      [BlacklistParameters.SortKeySelector]: request.SortKeySelector,
      [BlacklistParameters.SortOrder]: request.SortOrder,
    };

    if (request.From) {
      requestParams[BlacklistParameters.From] = request.From;
    }

    if (request.To) {
      requestParams[BlacklistParameters.To] = request.To;
    }

    if (request.Reason) {
      requestParams[BlacklistParameters.Reason] = request.Reason;
    }

    try {
      const response = await apiClientWithApi.get<BlacklistResponse>(
        'blackphone',
        {
          params: requestParams,
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue('Не удалось получить черный список номеров!');
    }
  },
);

export const removeFromBlacklist = createAsyncThunk(
  'Blacklist/removeFromBlacklist',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.delete<BlacklistResponse>(
        `blackphone/${encodeURIComponent(phone)}`,
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue('Не удалось удалить номер из черного списка!');
    }
  },
);

export const blacklistSlice = createSlice({
  name: 'blacklist',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getBlacklist.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getBlacklist.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.items = action.payload.items;
        state.totalItemsCount = action.payload.totalItemsCount;
        return state;
      })
      .addCase(getBlacklist.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError =
          'Не удалось загрузить список заблокированных номеров';
        return state;
      })
      .addCase(removeFromBlacklist.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(removeFromBlacklist.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.items = action.payload.items;
        return state;
      })
      .addCase(removeFromBlacklist.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError =
          'Не удалось удалить номер из черного списка';
        return state;
      });
  },
});

export default blacklistSlice.reducer;

export const selectBlacklist = (state: RootState) => state.blacklist.items;

export const totalBlacklist = (state: RootState) =>
  state.blacklist.totalItemsCount;

export const blacklistStatus = (state: RootState) => state.blacklist.status;
